<script setup lang="ts">
/**
 * `<Icon name="..." />` — single icon entrypoint for the editor.
 *
 * Mirrors `Content/Controls/Icon*.prefab`. All icon images are copied
 * verbatim from the Unity desktop project (`Content/Icons/Square/*.png`
 * and `Content/Icons/*.png`) into `src/assets/icons/` and are addressed
 * by kebab-case name (e.g. `add` → `add.png`, `eye-off` → `eye-off.png`).
 *
 * The PNGs are white-on-transparent, so we render them via a CSS `mask-image`
 * driven by `background-color: currentColor`. That lets the same asset
 * pick up its surrounding text color (muted in panel chrome, accent when
 * active, etc) without re-exporting per-color variants. PNGs are a
 * temporary stand-in.
 *
 * **Local SVGs resolve too, but they lose a name collision to the PNGs.**
 * Two stray SVGs predate this glob (`Attachment.svg` beside a live
 * `Attachment.png`), and they are leftovers rather than a migration in
 * progress — so a name carried by both artworks keeps rendering the PNG it
 * renders today. A real reauthoring deletes the PNG, at which point there is
 * no collision left to arbitrate. Unlike the padded PNGs, a local SVG is
 * authored edge-to-edge, so it skips the padding compensation below and its
 * `size` is the visible glyph size.
 *
 * **Font Awesome fallback (`fa:` prefix).** The Unity set only covers glyphs
 * the desktop app needed, so web-only surfaces sometimes have no honest icon to
 * reuse — reaching for a near-miss Unity glyph is worse than a correct generic
 * one (the scene-statistics toggle first borrowed `header_hierarchy`, which
 * reads as a hamburger). Those cases address Font Awesome Free Solid as
 * `<Icon name="fa:gauge-high" />`. The prefix is required, so a FA name can
 * never silently shadow a Unity icon, and it keeps "this is not upstream art"
 * visible at the call site. Same mask-image + `currentColor` path as the PNGs,
 * so sizing and tinting behave identically — except FA ships edge-to-edge SVG,
 * so it skips the PNG padding compensation below.
 */
import { computed } from "vue";

defineOptions({ name: "Icon" });
const props = withDefaults(
  defineProps<{
    name: string;
    size?: number;
    color?: string;
    /**
     * When true, render the PNG as a regular raster image (preserving
     * source RGB) instead of a recolored CSS mask. Use for artworks like
     * brand logos where the source color matters.
     */
    colored?: boolean;
  }>(),
  { size: 16, colored: false },
);

const ICONS = import.meta.glob("../../assets/icons/*.png", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

/**
 * Hand-authored SVGs living beside the Unity PNGs — art the desktop set has no
 * counterpart for, drawn for a web-only surface (today: `file-pdf`, the Spark
 * citation glyph). Consulted only AFTER the PNG map, so adding one can never
 * change what an existing name renders.
 */
const LOCAL_SVG_ICONS = import.meta.glob("../../assets/icons/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

/**
 * Lookups are normalized to lowercase so the map covers both the Unity
 * PNGs copied verbatim with PascalCase names (`Settings.png`,
 * `Share.png`, `Cube.png`, …) and the lowercase / kebab-case names the
 * components reference (`settings`, `share`, `cube`, …). When both a
 * lowercase and a PascalCase variant exist for the same name we keep the
 * first one the glob returns; rename the loser if you need a specific
 * artwork to win.
 */
const ICON_BY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(ICONS).map(([path, url]) => {
    const file = path.split("/").pop() ?? "";
    const base = file.replace(/\.png$/i, "").toLowerCase();
    return [base, url];
  }),
);

const ICON_ALIASES: Record<string, string> = {
  "chevron-left": "back",
  "chevron-right": "arrow",
  "chevron-down": "down",
  "chevron-up": "up",
};

/**
 * Font Awesome Free, `fa:` = Solid and `far:` = Regular. Both globs are
 * lazy-free (eager URLs, not inlined content), so Vite emits one asset per
 * referenced file and the unreferenced remainder of each set is dropped at
 * build time — the dependency does not ship ~1 500 unused glyphs.
 *
 * **Check the file exists before promising a weight.** Free ships all ~1 400
 * Solid glyphs but only 273 Regular ones, and the regular weight of a typical
 * UI glyph is Pro-only — there is no fallback between the two, so `far:` on a
 * name Free does not carry renders the missing-icon box, not the solid one.
 */
const FA_ICONS = import.meta.glob(
  "../../../node_modules/@fortawesome/fontawesome-free/svgs/solid/*.svg",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;

const FA_REGULAR_ICONS = import.meta.glob(
  "../../../node_modules/@fortawesome/fontawesome-free/svgs/regular/*.svg",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;

const FA_PREFIX = "fa:";
const FA_REGULAR_PREFIX = "far:";

function byFileName(icons: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(icons).map(([path, url]) => {
      const file = path.split("/").pop() ?? "";
      return [file.replace(/\.svg$/i, "").toLowerCase(), url];
    }),
  );
}

const LOCAL_SVG_BY_NAME = byFileName(LOCAL_SVG_ICONS);
const FA_BY_NAME = byFileName(FA_ICONS);
const FA_REGULAR_BY_NAME = byFileName(FA_REGULAR_ICONS);

/**
 * Source PNGs are exported at 32×32 with ~30% transparent padding baked in,
 * so the glyph itself only fills ~60% of the box. We multiply the requested
 * `size` (which expresses the *visible glyph* size) by this factor so the
 * container is grown to compensate. When the icons are reauthored as edge-
 * to-edge SVGs this can drop to 1.
 */
const PNG_PADDING_SCALE = 2;

const isFontAwesome = computed(() => {
  const name = props.name.toLowerCase();
  // `far:` is tested first only for legibility — it cannot be mistaken for
  // `fa:`, since the third character differs.
  return name.startsWith(FA_REGULAR_PREFIX) || name.startsWith(FA_PREFIX);
});

const url = computed(() => {
  const name = props.name.toLowerCase();
  if (name.startsWith(FA_REGULAR_PREFIX)) {
    return FA_REGULAR_BY_NAME[name.slice(FA_REGULAR_PREFIX.length)] ?? "";
  }
  if (name.startsWith(FA_PREFIX)) return FA_BY_NAME[name.slice(FA_PREFIX.length)] ?? "";
  const local = ICON_ALIASES[name] ?? name;
  return ICON_BY_NAME[local] ?? LOCAL_SVG_BY_NAME[local] ?? "";
});
/**
 * True for art authored edge-to-edge — Font Awesome, and a local SVG that won
 * the lookup above. The test mirrors that lookup exactly (PNG first), so a name
 * carried by both artworks is measured as the PNG it actually renders.
 */
const isEdgeToEdgeArt = computed(() => {
  if (isFontAwesome.value) return true;
  const name = props.name.toLowerCase();
  const local = ICON_ALIASES[name] ?? name;
  return !(local in ICON_BY_NAME) && local in LOCAL_SVG_BY_NAME;
});

// Edge-to-edge art already expresses the visible glyph size; only the padded
// Unity PNGs need compensating.
const renderSize = computed(() =>
  isEdgeToEdgeArt.value ? props.size : Math.round(props.size * PNG_PADDING_SCALE),
);
const style = computed(() => {
  const u = url.value;
  if (!u) return {};
  return {
    width: `${renderSize.value}px`,
    height: `${renderSize.value}px`,
    color: props.color,
    "--cf-icon-url": `url("${u}")`,
  } as Record<string, string>;
});
</script>

<template>
  <img
    v-if="url && colored"
    class="cf-icon cf-icon--raster"
    :src="url"
    :style="{ width: `${renderSize}px`, height: `${renderSize}px` }"
    :data-icon="name"
    alt=""
    aria-hidden="true"
  />
  <span
    v-else-if="url"
    class="cf-icon"
    :style="style"
    :data-icon="name"
    aria-hidden="true"
  />
  <span
    v-else
    class="cf-icon cf-icon--missing"
    :title="`Missing icon: ${name}`"
    :style="{ width: `${renderSize}px`, height: `${renderSize}px` }"
    aria-hidden="true"
  />
</template>

<style scoped>
.cf-icon {
  display: inline-block;
  vertical-align: middle;
  flex: 0 0 auto;
  background-color: currentColor;
  -webkit-mask-image: var(--cf-icon-url);
  mask-image: var(--cf-icon-url);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.cf-icon--raster {
  background-color: transparent;
  -webkit-mask-image: none;
  mask-image: none;
  object-fit: contain;
}

.cf-icon--missing {
  background: transparent;
  border: 1px dashed currentColor;
  opacity: 0.4;
  border-radius: var(--cf-radius-2);
}
</style>
